import type { CallStateAggregate } from "@theirstory/common/src/call_state/types";
import { produce } from "immer";

import {
  CALL_LOCAL_RECORDING_UPLOAD_COMPLETION,
  CALL_LOCAL_RECORDING_UPLOAD_FAILED,
  CALL_LOCAL_RECORDING_UPLOAD_INFO,
  CALL_LOCAL_RECORDING_UPLOAD_PENDING,
  CALL_LOCAL_RECORDING_UPLOAD_START,
  CALL_STATE_UPDATED,
} from "../actions/types";
import type { LocalAudioStream } from "../../lib/localRecording";
import type { PartsInfoEvent, PartUploadProgressEvent } from "../../lib/localRecordingNew";

type UploadState = "uploading" | "completed" | "failed";

export type LocalRecordingStreamStatus = PartUploadProgressEvent &
  PartsInfoEvent & { state: UploadState } & Pick<
    LocalAudioStream,
    "storyId" | "userId" | "recordingStartTime" | "recordingStopTime" | "type" | "id" | "streamId"
  >;

type LRState = {
  streams: { [key: string]: LocalRecordingStreamStatus };

  // TODO: Refactor all the bits of code that depend on these denormalized
  // arrays to instead just look at the source of truth above
  pending: LocalRecordingStreamStatus[];
  uploads: LocalRecordingStreamStatus[];
  completions: LocalRecordingStreamStatus[];
  callStates: { [key: string]: CallStateAggregate };
};

const initialState: LRState = {
  streams: {},
  pending: [],
  uploads: [],
  completions: [],
  callStates: {},
};

const actionsToWatch = [
  CALL_LOCAL_RECORDING_UPLOAD_PENDING,
  CALL_LOCAL_RECORDING_UPLOAD_INFO,
  CALL_LOCAL_RECORDING_UPLOAD_START,
  CALL_LOCAL_RECORDING_UPLOAD_COMPLETION,
  CALL_LOCAL_RECORDING_UPLOAD_FAILED,
  CALL_STATE_UPDATED,
];

const localRecordingReducer = produce(
  (
    draft: LRState,
    action: {
      type: string;
      data: { stream: LocalAudioStream } & PartUploadProgressEvent & PartsInfoEvent & { callState: CallStateAggregate };
    }
  ) => {
    if (!actionsToWatch.includes(action.type)) {
      return draft;
    }
    const streamStatus: LocalRecordingStreamStatus = {
      state: "uploading",
      ...(draft.streams[action.data.stream.id] || {}),
      storyId: action.data.stream.storyId,
      userId: action.data.stream.userId,
      recordingStartTime: action.data.stream.recordingStartTime,
      recordingStopTime: action.data.stream.recordingStopTime,
      type: action.data.stream.type,
      streamId: action.data.stream.streamId,
      partsUploaded: action.data.partsUploaded || draft.streams?.[action.data.stream.id]?.partsUploaded,
      uploadedBytes: action.data.uploadedBytes || draft.streams?.[action.data.stream.id]?.uploadedBytes,
      partBytes: action.data.partBytes || draft.streams?.[action.data.stream.id]?.partBytes,
      parts: action.data.parts || draft.streams?.[action.data.stream.id]?.parts,
      id: action.data.stream.id,
    };
    switch (action.type) {
      case CALL_LOCAL_RECORDING_UPLOAD_COMPLETION:
        streamStatus.state = "completed";
        break;
      case CALL_LOCAL_RECORDING_UPLOAD_FAILED:
        streamStatus.state = "failed";
        break;
      case CALL_STATE_UPDATED: {
        draft.callStates[action.data.stream.storyId] = action.data.callState;
        break;
      }
      default:
        break;
    }
    draft.streams[action.data.stream.id] = streamStatus;

    draft.pending = [];
    draft.uploads = Object.values(draft.streams);
    draft.completions = [];

    for (const stream of Object.values(draft.streams)) {
      if (stream.state === "uploading") {
        draft.pending.push(stream);
      } else if (stream.state === "completed") {
        draft.completions.push(stream);
      }
    }

    return draft;
  },
  initialState
);

export default localRecordingReducer;
